import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import "./UsedCarDecisionPage.css";

const GET_MENU_API_URL = awsConfig.api_url_get_menu;

const defaultFormState = {
  storeSlug: "should-i-view-this-car",
  brandName: "Revmarket",
  headline: "Should you view this car?",
  subheadline:
    "Paste the listing or registration and get a clear view/no-view recommendation, seller message, and price context before you travel.",
  ctaLabel: "Check this car",
};

function isStarterOnlyPlan(plan) {
  return String(plan || "").trim().toLowerCase() === "starter";
}

export default function UsedCarDecisionPage({ business }) {
  const [formState, setFormState] = useState(defaultFormState);
  const [publishes, setPublishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [publishResult, setPublishResult] = useState(null);
  const isStarter = useMemo(() => isStarterOnlyPlan(business?.plan || business?.planType), [business]);

  const getBearer = useCallback(async () => {
    const { tokens } = await fetchAuthSession();
    const bearer = resolveAuthToken(tokens);
    if (!bearer) throw new Error("Session expired. Please sign in again.");
    return bearer;
  }, []);

  const loadPublishes = useCallback(async () => {
    if (!business?.businessId) return;
    setLoading(true);
    setError("");
    try {
      const bearer = await getBearer();
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || "Failed to load your published links.");
      const active = body?.publishes?.active || [];
      const filtered = active.filter((entry) => (entry.template || "").toLowerCase() === "used_car_decision");
      setPublishes(filtered);
    } catch (err) {
      setError(err.message || "Failed to load your published links.");
    } finally {
      setLoading(false);
    }
  }, [business?.businessId, getBearer]);

  useEffect(() => {
    loadPublishes();
  }, [loadPublishes]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormState((current) => ({ ...current, [name]: value }));
  };

  const publishFlow = async (event) => {
    event.preventDefault();
    if (!isStarter) {
      setError("This flow is available on the Starter plan only.");
      return;
    }
    setPublishing(true);
    setError("");
    setPublishResult(null);
    try {
      const bearer = await getBearer();
      const response = await fetch(`${GET_MENU_API_URL}/${business.businessId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${bearer}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template: "used_car_decision",
          storeSlug: formState.storeSlug,
          items: [],
          templateMeta: {
            brandName: formState.brandName,
            headline: formState.headline,
            subheadline: formState.subheadline,
            ctaLabel: formState.ctaLabel,
            poweredByLabel: "Powered by Layerbound",
          },
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "Could not publish the flow.");
      setPublishResult(body);
      await loadPublishes();
    } catch (err) {
      setError(err.message || "Could not publish the flow.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="used-car-page">
      <div className="used-car-page__header">
        <div>
          <p className="used-car-page__eyebrow">Layerbound Template</p>
          <h1>Should I View This Car?</h1>
          <p className="used-car-page__copy">
            Publish a branded used-car decision link that gives buyers a clear vehicle decision, seller message, and price context before they travel.
          </p>
        </div>
        <Link className="used-car-page__back" to="/">
          Back to dashboard
        </Link>
      </div>

      {!isStarter && (
        <section className="used-car-page__notice">
          <h2>Starter plan required</h2>
          <p>
            This flow is gated to Starter accounts only. Upgrade the business to Starter before publishing or managing this template.
          </p>
        </section>
      )}

      <div className="used-car-page__grid">
        <section className="used-car-page__card">
          <h2>Publish link</h2>
          <p className="used-car-page__muted">
            Keep the defaults for Revmarket, or set the public brand to the dealership that owns the link.
          </p>
          <form className="used-car-page__form" onSubmit={publishFlow}>
            <label>
              Public slug
              <input name="storeSlug" value={formState.storeSlug} onChange={updateField} placeholder="should-i-view-this-car" />
            </label>
            <label>
              Public brand
              <input name="brandName" value={formState.brandName} onChange={updateField} placeholder="Revmarket" />
            </label>
            <label>
              Headline
              <input name="headline" value={formState.headline} onChange={updateField} />
            </label>
            <label>
              Subheadline
              <textarea name="subheadline" value={formState.subheadline} onChange={updateField} rows={4} />
            </label>
            <label>
              CTA label
              <input name="ctaLabel" value={formState.ctaLabel} onChange={updateField} />
            </label>
            <button type="submit" disabled={publishing || !isStarter}>
              {publishing ? "Publishing…" : "Publish decision link"}
            </button>
          </form>
          {error ? <p className="used-car-page__error">{error}</p> : null}
          {publishResult?.publicUrl ? (
            <div className="used-car-page__result">
              <span>Latest link</span>
              <a href={publishResult.publicUrl} target="_blank" rel="noreferrer">
                {publishResult.publicUrl}
              </a>
            </div>
          ) : null}
        </section>

        <section className="used-car-page__card">
          <h2>Live links</h2>
          <p className="used-car-page__muted">
            Public links published from this business for the used-car decision template.
          </p>
          {loading ? <p className="used-car-page__muted">Loading links…</p> : null}
          {!loading && !publishes.length ? <p className="used-car-page__muted">No live used-car links yet.</p> : null}
          <div className="used-car-page__links">
            {publishes.map((entry) => (
              <article key={entry.publishedAt} className="used-car-page__link-card">
                <div>
                  <strong>{entry.storeSlug || entry.storeId}</strong>
                  <p>Published {new Date(entry.publishedAt).toLocaleDateString()}</p>
                </div>
                <a href={entry.htmlUrl || entry.publicUrl} target="_blank" rel="noreferrer">
                  Open link
                </a>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
